/**
 * SoleadLab 2.0 — Copilot API Client (E2.3)
 *
 * Thin fetch wrappers for the copilot endpoints.
 * All copilot logic is backend-owned; this service is strictly read/query.
 */

import { apiFetch } from './apiFetch';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface GroundingReference {
  ref_id: string;
  source_type: string;
  source_id: string;
  field: string | null;
  value: unknown;
  claim: string;
}

export interface QualityContext {
  overall_score: number | null;
  meets_preferred_threshold: boolean;
  meets_minimum_threshold: boolean;
  warning_text: string | null;
}

export interface UncertaintyStatement {
  statement: string;
  reason: string;
}

export interface GroundingJSON {
  grounding_version: string;
  grounded: boolean;
  references: GroundingReference[];
  quality_context: QualityContext;
  uncertainty_statements: UncertaintyStatement[];
}

export interface CopilotQueryResult {
  answered: boolean;
  query_id: string;
  // Present when answered === true
  answer_id?: string;
  answer_text?: string;
  confidence_score?: number;
  grounding?: GroundingJSON;
  quality_warning?: string | null;
  // Present when answered === false
  refusal_reason?: string;
  assembled?: boolean;
  eligible?: boolean;
  warnings?: string[];
}

export interface QueryHistoryEntry {
  query_id: string;
  query_text: string;
  created_at: string;
  eligible: boolean;
}

// ─── API functions ──────────────────────────────────────────────────────────

export async function submitCopilotQuery(
  siteId: string,
  queryText: string,
  assetId?: string,
): Promise<CopilotQueryResult> {
  return apiFetch<CopilotQueryResult>(
    `/api/v2/sites/${siteId}/copilot/query`,
    {
      method: 'POST',
      body: JSON.stringify({
        query_text: queryText,
        ...(assetId ? { asset_id: assetId } : {}),
      }),
    },
  );
}

export async function listCopilotQueries(
  siteId: string,
  limit = 20,
  offset = 0,
): Promise<{ items: QueryHistoryEntry[]; total: number }> {
  return apiFetch<{ items: QueryHistoryEntry[]; total: number }>(
    `/api/v2/sites/${siteId}/copilot/queries?limit=${limit}&offset=${offset}`,
  );
}
